import { useEffect, useMemo, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import {
    Sparkles,
    ShieldCheck,
    Copy,
    Printer,
    Mail,
    Trophy,
    Users,
} from "lucide-react";

/**
 * ReferralModal — the primary member's referral generation & share surface.
 *
 * It shows:
 *  - The member's unique referral code + a QR pointing at /join?ref=CODE
 *  - Perx branding + key member benefits
 *  - Copy link / Print / Email actions
 *  - A live progress strip (X / next threshold → auto-applied % off)
 */
export default function ReferralModal({ open, onClose, member }) {
    const [ref, setRef] = useState(null);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [emailTo, setEmailTo] = useState("");
    const [emailNote, setEmailNote] = useState("");
    const [emailBusy, setEmailBusy] = useState(false);
    const printAreaRef = useRef(null);

    useEffect(() => {
        if (!open) return;

        let cancelled = false;

        const loadReferral = async () => {
            setLoading(true);

            try {
                // Admin portal: use the selected member's referral data.
                if (member?.referral_code) {
                    setRef({
                        code: member.referral_code,
                        referral_url: member.referral_url,
                        user_id: member.id,
                        member_name: member.name,
                        created_at: member.referral_created_at,
                    });

                    // Do not show the logged-in user's statistics for another member.
                    setStats(null);
                    return;
                }

                // Member portal: load the authenticated member's referral details.
                const [referralResponse, statsResponse] = await Promise.all([
                    api.post("/member/referrals/mine"),
                    api.get("/member/referrals/stats"),
                ]);

                if (cancelled) return;

                setRef(referralResponse.data);
                setStats(statsResponse.data);
            } catch (error) {
                if (!cancelled) {
                    toast.error(formatApiError(error));
                    setRef(null);
                    setStats(null);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        loadReferral();

        return () => {
            cancelled = true;
        };
    }, [open, member]);

    const joinUrl = useMemo(() => {
        if (!ref?.code) return "";

        return ref.referral_url ||
            `${window.location.origin}/join?ref=${encodeURIComponent(ref.code)}`;
    }, [ref]);

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(joinUrl);
            toast.success("Referral link copied");
        } catch {
            toast.error("Couldn't copy — please copy the link manually.");
        }
    };

    const printCard = () => {
        // Isolate the referral card into a hidden print iframe so we don't
        // print the whole app shell around it.
        if (!printAreaRef.current) return;
        const html = printAreaRef.current.innerHTML;
        const w = window.open("", "_blank", "width=820,height=900");
        if (!w) {
            toast.error("Popup blocked — allow popups to print.");
            return;
        }
        w.document.write(`<!doctype html><html><head><title>Perx referral</title>
<style>
  body{font-family:Manrope,Arial,sans-serif;background:#FAFAF7;color:#001c50;padding:40px;}
  h1,h2{font-family:'Playfair Display',Georgia,serif;color:#001c50;}
  .card{max-width:640px;margin:0 auto;background:#fff;border:1px solid #eee;
        border-radius:24px;padding:32px;box-shadow:0 8px 40px rgba(0,28,80,.08);}
  .code{font-family:'Courier New',monospace;font-size:32px;letter-spacing:4px;
        background:#001c50;color:#FAFAF7;padding:12px 18px;border-radius:12px;
        display:inline-block;}
  ul{line-height:1.8;} .brand{color:#D4AF37;font-weight:700;}
  @media print{body{padding:0}}
</style></head><body><div class="card">${html}</div></body></html>`);
        w.document.close();
        setTimeout(() => {
            w.focus();
            w.print();
        }, 300);
    };

    const sendEmail = async (e) => {
        e.preventDefault();
        if (!emailTo.trim()) return;
        setEmailBusy(true);
        try {
            await api.post("/member/referrals/email", {
                to_email: emailTo.trim(),
                personal_note: emailNote.trim() || null,
            });
            toast.success(`Invite sent to ${emailTo.trim()}`);
            setEmailTo("");
            setEmailNote("");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setEmailBusy(false);
        }
    };

    const conversions = stats?.conversions_count ?? 0;
    const next = stats?.next_tier;
    const current = stats?.current_tier;
    const override = stats?.admin_override_tier;
    const progress = next
        ? Math.min(100, Math.round((conversions / next.threshold) * 100))
        : 100;

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent
                data-testid="referral-modal"
                className="max-w-2xl bg-cream border-navy-100 max-h-[92vh] overflow-y-auto"
            >
                <DialogHeader>
                    <DialogTitle className="font-display text-3xl text-navy-700 flex items-center gap-2">
                        <Trophy size={22} className="text-gold-600" />
                        Refer a friend, earn a discount
                    </DialogTitle>
                    <DialogDescription className="text-navy-500">
                        {member?.name
                            ? `Referral code for ${member.name}. Share this unique link with prospective Perx members.`
                            : "Share this unique referral link with prospective Perx members."}
                    </DialogDescription>
                </DialogHeader>

                {loading || !ref ? (
                    <div className="py-16 text-center text-navy-500 text-sm">
                        Loading your referral card…
                    </div>
                ) : (
                    <div className="space-y-6">
                        {/* Progress strip */}
                        <div
                            data-testid="referral-progress"
                            className="rounded-2xl border border-gold-200/60 bg-white p-4"
                        >
                            <div className="flex items-center justify-between text-xs text-navy-500">
                                <span className="flex items-center gap-1.5">
                                    <Users size={14} className="text-navy-500" />
                                    {conversions} paid referral{conversions === 1 ? "" : "s"}
                                </span>
                                <span>
                                    {override ? (
                                        <Badge className="bg-navy-700 text-cream">
                                            Admin override: {override.discount_pct}% off
                                        </Badge>
                                    ) : current ? (
                                        <Badge className="bg-gold-500 text-navy-800">
                                            Now saving {current.discount_pct}% · {current.name}
                                        </Badge>
                                    ) : next ? (
                                        <span className="text-navy-500">
                                            {next.threshold - conversions} more to unlock{" "}
                                            <span className="font-semibold text-navy-700">
                                                {next.discount_pct}% off
                                            </span>
                                        </span>
                                    ) : (
                                        <span className="text-navy-500">Top tier reached.</span>
                                    )}
                                </span>
                            </div>
                            {!override && (
                                <div className="mt-2 h-1.5 rounded-full bg-navy-50 overflow-hidden">
                                    <div
                                        className="h-full bg-gold-500 transition-all"
                                        style={{ width: `${progress}%` }}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Printable / shareable card */}
                        <div ref={printAreaRef} className="rounded-2xl border border-navy-100 bg-white p-6">
                            <div className="flex items-start gap-6">
                                <div className="flex-shrink-0 rounded-xl border border-navy-100 bg-white p-3">
                                    <QRCodeCanvas
                                        value={joinUrl}
                                        size={128}
                                        level="M"
                                        bgColor="#ffffff"
                                        fgColor="#001c50"
                                        data-testid="referral-qr"
                                    />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-[11px] uppercase tracking-widest text-gold-600">
                                        Perx Group
                                    </div>
                                    <h2 className="font-display text-2xl text-navy-700 leading-tight mt-1">
                                        Join Perx, <span className="brand text-gold-600">panda-approved</span> pricing on things you already buy.
                                    </h2>
                                    <div className="mt-3 flex items-center gap-2">
                                        <span
                                            data-testid="referral-code"
                                            className="code inline-flex items-center gap-2 rounded-lg bg-navy-700 text-cream px-3 py-1.5 font-mono tracking-widest text-sm"
                                        >
                                            {ref.code}
                                        </span>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={copyLink}
                                            data-testid="referral-copy-btn"
                                            className="rounded-full border-navy-200 h-8 text-xs"
                                        >
                                            <Copy size={12} className="mr-1.5" />
                                            Copy link
                                        </Button>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-6 grid sm:grid-cols-2 gap-3 text-sm text-navy-500">
                                <BenefitTile
                                    icon={Sparkles}
                                    title="Every deal unlocked"
                                    body="Vetted vendors across insurance, travel, health & lifestyle."
                                />
                                <BenefitTile
                                    icon={ShieldCheck}
                                    title="Panda Promise"
                                    body="30-day refund if you find a better public price."
                                />
                            </div>

                            <div className="mt-6 text-[11px] text-navy-400">
                                Scan the QR or visit{" "}
                                <span className="text-navy-700 font-semibold">
                                    {joinUrl}
                                </span>
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="grid sm:grid-cols-2 gap-3">
                            <Button
                                onClick={printCard}
                                variant="outline"
                                data-testid="referral-print-btn"
                                className="rounded-full h-11 border-navy-200"
                            >
                                <Printer size={14} className="mr-2" />
                                Print card
                            </Button>
                            <Button
                                onClick={copyLink}
                                data-testid="referral-copy-btn-2"
                                className="rounded-full h-11 bg-navy-700 hover:bg-navy-800 text-cream"
                            >
                                <Copy size={14} className="mr-2" />
                                Copy referral link
                            </Button>
                        </div>

                        {/* Email invite */}
                        <form
                            onSubmit={sendEmail}
                            data-testid="referral-email-form"
                            className="rounded-2xl border border-navy-100 bg-white p-5 space-y-3"
                        >
                            <div className="flex items-center gap-2 text-navy-700 font-semibold text-sm">
                                <Mail size={14} className="text-gold-600" />
                                Email this invite to a friend
                            </div>
                            <Input
                                type="email"
                                required
                                placeholder="friend@example.com"
                                value={emailTo}
                                onChange={(e) => setEmailTo(e.target.value)}
                                data-testid="referral-email-to"
                                className="h-11 bg-cream border-navy-100"
                            />
                            <Textarea
                                rows={3}
                                placeholder="Optional personal note — e.g. 'You'd love the pet insurance deal here.'"
                                value={emailNote}
                                onChange={(e) => setEmailNote(e.target.value)}
                                data-testid="referral-email-note"
                                className="bg-cream border-navy-100 resize-none"
                            />
                            <Button
                                type="submit"
                                disabled={emailBusy || !emailTo.trim()}
                                data-testid="referral-email-send"
                                className="rounded-full w-full bg-gold-500 hover:bg-gold-600 text-navy-800 h-11 disabled:opacity-50"
                            >
                                {emailBusy ? "Sending…" : "Send invite"}
                            </Button>
                        </form>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

function BenefitTile({ icon: Icon, title, body }) {
    return (
        <div className="rounded-xl bg-cream border border-navy-100 p-3">
            <div className="flex items-center gap-1.5 text-navy-700 font-semibold text-xs">
                <Icon size={12} className="text-gold-600" />
                {title}
            </div>
            <div className="text-[11px] text-navy-500 mt-1 leading-relaxed">
                {body}
            </div>
        </div>
    );
}
